import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Typography,
  Paper,
  Alert,
  Button,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import Message from './Message';

const MAX_MESSAGE_LENGTH = 1000;

/**
 * Chatbox — Core chat UI component for the AI Trading Assistant.
 *
 * @param {Array} messages - Array of message objects { id, role, content, createdAt }
 * @param {function} onSendMessage - Callback when user sends a message
 * @param {boolean} loading - Whether an AI response is being generated
 * @param {string|null} error - Error message to display, or null
 * @param {function} onRetry - Callback when user clicks retry on error
 * @param {boolean} disabled - Whether the chatbox is disabled
 */
const Chatbox = ({ messages = [], onSendMessage, loading, error, onRetry, disabled }) => {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to latest message
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading, error]);

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || disabled || loading) return;
    onSendMessage(trimmed);
    setInput('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_MESSAGE_LENGTH) {
      setInput(value);
    }
  };

  const isInputEmpty = input.trim().length === 0;

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Message List */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 2,
          py: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        {messages.length === 0 && !loading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography variant="body1" color="text.secondary" textAlign="center">
              Ask me anything — options strategies, trade setups, market analysis, or any stock question.
            </Typography>
          </Box>
        ) : (
          <>
            {messages.map((msg) => (
              <Message key={msg.id} message={msg} />
            ))}
            {loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, pl: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Thinking...
                </Typography>
              </Box>
            )}
            {error && (
              <Alert
                severity="error"
                sx={{ mt: 1 }}
                action={
                  <Button color="inherit" size="small" onClick={onRetry}>
                    Retry
                  </Button>
                }
              >
                {error}
              </Alert>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input Area */}
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder="Ask about any stock, options strategy, or market outlook..."
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={disabled || loading}
          slotProps={{
            htmlInput: { maxLength: MAX_MESSAGE_LENGTH },
          }}
          helperText={
            input.length > 0
              ? `${input.length}/${MAX_MESSAGE_LENGTH}`
              : undefined
          }
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={disabled || loading || isInputEmpty}
          aria-label="Send message"
          sx={{
            mb: input.length > 0 ? 2.5 : 0,
            bgcolor: 'primary.main',
            color: '#fff',
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'action.disabled' },
          }}
        >
          <Send fontSize="small" />
        </IconButton>
      </Box>
    </Paper>
  );
};

export default Chatbox;
